import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@/App.css';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import Orb from '@/components/Orb';
import orbAudio from '@/components/orbAudio';
import Intro from '@/components/Intro';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navegación orbital: desactivada por feedback del cliente (11-jul-2026).
// Para reactivarla, cambiar a true. Los chips disparan sendMessage(item.q).
const ORBITAL_NAV_ENABLED = false;

const BG_VIDEOS = ['/bg-01.mp4', '/bg-02.mp4', '/bg-03.mp4'];

function BgCycler({ active }) {
  const videoRef = useRef(null);
  const srcRef = useRef(null);
  const timers = useRef([]);
  const [src, setSrc] = useState(null);
  const [phase, setPhase] = useState('hidden'); // hidden | in | out
  const [glitching, setGlitching] = useState(false);

  useEffect(() => { srcRef.current = src; }, [src]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    video.load();
    const tryPlay = () => video.play().catch(() => {});
    video.addEventListener('loadeddata', tryPlay, { once: true });
    return () => video.removeEventListener('loadeddata', tryPlay);
  }, [src]);

  useEffect(() => {
    const onVis = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else if (srcRef.current) v.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!active) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.innerWidth < 720) return; // móvil: solo gradiente (batería y datos)

    let cancelled = false;
    const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); };
    const video = videoRef.current;

    const microGlitch = () => {
      setGlitching(true);
      later(() => { if (!cancelled) setGlitching(false); }, 240);
    };

    // reel: entra desde abajo, vive su duración exacta y sale hacia arriba
    const appear = () => {
      if (cancelled) return;
      const pool = BG_VIDEOS.filter(v => v !== srcRef.current);
      const target = pool[Math.floor(Math.random() * pool.length)];
      setSrc(target);
      setPhase('in');
      later(microGlitch, 720); // flick en el snap
    };

    const disappear = () => {
      if (cancelled) return;
      setPhase('out');
      microGlitch();
      later(() => {
        if (cancelled) return;
        setPhase('hidden');
        setSrc(null);
      }, 740);
      schedule(false);
    };

    const schedule = (first) => {
      const wait = first
        ? 5000 + Math.random() * 7000
        : 10000 + Math.random() * 16000;
      later(appear, wait);
    };

    const onEnded = () => disappear();
    const onError = () => { setPhase('hidden'); setSrc(null); schedule(false); };
    if (video) {
      video.addEventListener('ended', onEnded);
      video.addEventListener('error', onError);
    }

    schedule(true);
    return () => {
      cancelled = true;
      timers.current.forEach(clearTimeout);
      timers.current = [];
      if (video) {
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onError);
      }
    };
  }, [active]);

  return (
    <div className={`bg-video-wrap ${glitching ? 'bg-glitching' : ''}`} aria-hidden="true">
      <video
        ref={videoRef}
        className={`bg-video ${phase === 'in' ? 'reel-in' : phase === 'out' ? 'reel-out' : ''}`}
        src={src || undefined}
        muted
        playsInline
        preload="auto"
      />
      <div className={`bg-video-veil ${phase !== 'hidden' ? 'on' : ''}`} />
    </div>
  );
}

const ORBITAL_ITEMS = [
  { label: 'historia', q: '¿Qué es La Botica y cuál es su historia?' },
  { label: 'equipo', q: '¿Quiénes conforman el equipo?' },
  { label: 'servicios', q: '¿Qué servicios ofrecen?' },
  { label: 'proyectos', q: 'Cuéntame de sus proyectos destacados' },
  { label: 'contacto', q: '¿Cómo puedo contactarlos?' },
];

function ScrambleTitle({ text, active }) {
  const [display, setDisplay] = useState(text);
  useEffect(() => {
    if (!active) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplay(text);
      return;
    }
    const chars = '!<>-_\\/[]{}=+*^?#';
    let frame = 0;
    const total = 34;
    const id = setInterval(() => {
      frame += 1;
      const resolved = Math.floor(text.length * (frame / total));
      let out = text.slice(0, resolved);
      for (let i = resolved; i < text.length; i += 1) {
        out += chars[Math.floor(Math.random() * chars.length)];
      }
      if (frame >= total) {
        setDisplay(text);
        clearInterval(id);
      } else {
        setDisplay(out);
      }
    }, 32);
    return () => clearInterval(id);
  }, [text, active]);
  return <h1 className="chat-title" aria-label={text}>{display}</h1>;
}

function SocialLinks({ className }) {
  return (
    <div className={className}>
      <a href="https://www.facebook.com/agencialabotica" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Facebook">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </a>
      <a href="https://www.instagram.com/agencialabotica" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="Instagram">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
      </a>
      <a href="https://www.youtube.com/@agencialabotica" target="_blank" rel="noopener noreferrer" className="social-link" aria-label="YouTube">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
      </a>
    </div>
  );
}

function Home() {
  const [introDone, setIntroDone] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hola. Soy **BoticarIA**, la inteligencia de La Botica. Antes de empezar, ¿cómo te llamas?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [orbState, setOrbState] = useState('idle');
  const [isRevealing, setIsRevealing] = useState(false);
  const revealTimer = useRef(null);
  const revealSkip = useRef(null);
  const soundOnRef = useRef(true);
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const speakingTimer = useRef(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasGivenName, setHasGivenName] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    return !localStorage.getItem('cookiesAccepted');
  });
  const [soundOn, setSoundOn] = useState(() => {
    return localStorage.getItem('boticariaSound') !== 'off';
  });

  useEffect(() => {
    orbAudio.setEnabled(soundOn);
    soundOnRef.current = soundOn;
    localStorage.setItem('boticariaSound', soundOn ? 'on' : 'off');
  }, [soundOn]);
  const [leadData, setLeadData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    consulta: '',
    acceptPrivacy: false
  });

  const handleAcceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setShowCookieBanner(false);
  };

  const suggestedQuestions = [
    '¿Qué es La Botica?',
    '¿Qué los hace únicos?',
    '¿Quiénes conforman el equipo?',
    '¿Qué servicios ofrecen?',
    '¿Cómo puedo contactarlos?'
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, showSuggestions, showLeadForm, isLoading]);

  // El orbe respira con la conversación
  useEffect(() => {
    if (isLoading) {
      setOrbState('thinking');
    } else if (orbState !== 'speaking') {
      setOrbState(input.trim() ? 'typing' : 'idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, input]);

  const triggerSpeaking = useCallback(() => {
    setOrbState('speaking');
    if (speakingTimer.current) clearTimeout(speakingTimer.current);
    speakingTimer.current = setTimeout(() => {
      setOrbState('idle');
    }, 3200);
  }, []);

  useEffect(() => () => {
    if (speakingTimer.current) clearTimeout(speakingTimer.current);
    if (revealTimer.current) clearTimeout(revealTimer.current);
  }, []);

  const playBoticariaVoice = useCallback(() => {
    if (sessionStorage.getItem('boticariaVoicePlayed')) return;
    if (!soundOnRef.current) return;
    const audio = new Audio('/boticaria-voice.mp3');
    audio.volume = 0.9;
    const attempt = () => {
      audio.play().then(() => {
        sessionStorage.setItem('boticariaVoicePlayed', '1');
        setOrbState('speaking');
        audio.onended = () => setOrbState('idle');
      }).catch(() => {
        const once = () => {
          window.removeEventListener('pointerdown', once);
          window.removeEventListener('keydown', once);
          if (!sessionStorage.getItem('boticariaVoicePlayed')) attempt();
        };
        window.addEventListener('pointerdown', once, { once: true });
        window.addEventListener('keydown', once, { once: true });
      });
    };
    attempt();
  }, []);

  const finishRevealNow = () => {
    if (revealSkip.current) revealSkip.current();
  };

  const afterReveal = (fullText) => {
    setIsRevealing(false);
    setOrbState('idle');

    if (!hasGivenName) {
      setHasGivenName(true);
      setTimeout(() => setShowSuggestions(true), 400);
    }

    const lowerResponse = fullText.toLowerCase();
    const askingForContactDetails = (
      lowerResponse.includes('te conecto con fran') &&
      lowerResponse.includes('nombre') &&
      lowerResponse.includes('email')
    );
    if (askingForContactDetails) {
      setShowLeadForm(true);
    }
  };

  const revealResponse = (fullText) => {
    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let idx = -1;
    setMessages(prev => {
      idx = prev.length;
      return [...prev, { role: 'assistant', content: reduced ? fullText : '' }];
    });

    if (reduced) {
      afterReveal(fullText);
      return;
    }

    setIsRevealing(true);
    setOrbState('speaking');

    const tokens = fullText.split(/(\s+)/);
    let i = 0;

    const writeAll = () => {
      if (revealTimer.current) clearTimeout(revealTimer.current);
      revealSkip.current = null;
      setMessages(prev => {
        const copy = [...prev];
        if (copy[idx]) copy[idx] = { role: 'assistant', content: fullText };
        return copy;
      });
      afterReveal(fullText);
    };
    revealSkip.current = writeAll;

    const step = () => {
      i = Math.min(i + 2, tokens.length);
      const partial = tokens.slice(0, i).join('');
      setMessages(prev => {
        const copy = [...prev];
        if (copy[idx]) copy[idx] = { role: 'assistant', content: partial };
        return copy;
      });
      if (i < tokens.length) {
        revealTimer.current = setTimeout(step, 34);
      } else {
        revealSkip.current = null;
        afterReveal(fullText);
      }
    };
    step();
  };

  const sendMessage = async (rawText) => {
    const userMessage = (rawText ?? input).trim();
    if (!userMessage || isLoading || isRevealing) return;

    setInput('');
    setShowSuggestions(false);

    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);
    orbAudio.startThinking();

    try {
      const historyToSend = newMessages.slice(1).map(m => ({ role: m.role, content: m.content }));

      const response = await axios.post(
        `${API}/chat`,
        {
          message: userMessage,
          session_id: sessionId,
          history: historyToSend.slice(0, -1)
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const lines = response.data.split('\n');
      let botResponse = '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.substring(6);
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              botResponse = parsed.content;
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }

      if (botResponse) {
        orbAudio.chime();
        revealResponse(botResponse);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Disculpa, hubo un error al procesar tu mensaje. Por favor intenta nuevamente.'
      }]);
    } finally {
      setIsLoading(false);
      orbAudio.stopThinking();
    }
  };

  const handleSuggestionClick = (question) => {
    sendMessage(question);
  };

  const handleSubmitLead = async (e) => {
    e.preventDefault();

    try {
      await axios.post(`${API}/lead`, leadData);

      orbAudio.chime();
      revealResponse(`Perfecto, ${leadData.nombre}. Francisca Novoa se pondrá en contacto contigo a la brevedad en ${leadData.email}. ¡Gracias por tu interés!`);

      setShowLeadForm(false);
      setLeadData({
        nombre: '',
        email: '',
        telefono: '',
        empresa: '',
        consulta: '',
        acceptPrivacy: false
      });
    } catch (error) {
      console.error('Error submitting lead:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Hubo un error al enviar tus datos. Por favor intenta nuevamente o escríbenos directamente a fran@agencialabotica.cl'
      }]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const conversationStarted = messages.length > 1;

  return (
    <div className={`app-container ${conversationStarted ? 'conversation-on' : ''}`}>
      {!introDone && <Intro onDone={() => { setIntroDone(true); playBoticariaVoice(); }} />}
      <div className="bg-gradient" />
      <BgCycler active={introDone} />
      <div className="bg-grid" />

      <Orb state={orbState} docked={conversationStarted} />

      {ORBITAL_NAV_ENABLED && introDone && !conversationStarted && (
        <nav className="orbit-ring" aria-label="Explorar La Botica">
          {ORBITAL_ITEMS.map((item, i) => (
            <div
              key={item.label}
              className="orbit-arm"
              style={{ '--a': `${(360 / ORBITAL_ITEMS.length) * i - 90}deg` }}
            >
              <div className="orbit-node">
                <button className="orbit-chip" onClick={() => sendMessage(item.q)}>
                  <span className="orbit-label" style={{ '--a': `${(360 / ORBITAL_ITEMS.length) * i - 90}deg` }}>{item.label}</span>
                </button>
              </div>
            </div>
          ))}
        </nav>
      )}

      <SocialLinks className="social-rail" />

      <header className="header">
        <div className="logo-block">
          <img src="/logo-labotica.svg" alt="Agencia La Botica" className="logo" />
        </div>
        <div className="header-right">
          <p className="tagline">Científicos &amp; Creativos</p>
          <button
            className="sound-toggle"
            onClick={() => setSoundOn(!soundOn)}
            aria-label={soundOn ? 'Silenciar sonido' : 'Activar sonido'}
          >
            {soundOn ? 'sonido ●' : 'sonido ○'}
          </button>
        </div>
      </header>

      <main className="main-container">
        <div className="chat-column">
          <div className="chat-identity">
            <span className="chat-eyebrow">Laboratorio conversacional</span>
            <ScrambleTitle text="BoticarIA" active={introDone} />
          </div>

          <div className="messages-scroll" onClick={finishRevealNow}>
            {messages.map((msg, index) => (
              <div key={index} className={`msg ${msg.role}`}>
                {msg.role === 'assistant' ? (
                  <>
                    <span className="msg-label">BoticarIA</span>
                    <div className="msg-body">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  </>
                ) : (
                  <div className="msg-body user-body">{msg.content}</div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="msg assistant">
                <span className="msg-label">BoticarIA</span>
                <div className="msg-body thinking-line">
                  <span className="thinking-text">procesando</span>
                  <span className="dot" /><span className="dot" /><span className="dot" />
                </div>
              </div>
            )}

            {showSuggestions && !isLoading && (
              <div className="suggestions">
                <p className="suggestions-label">¿Qué te gustaría saber?</p>
                <div className="suggestions-row">
                  {suggestedQuestions.map((question, index) => (
                    <button
                      key={index}
                      className="chip"
                      onClick={() => handleSuggestionClick(question)}
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showLeadForm && (
              <div className="msg assistant">
                <span className="msg-label">Contacto</span>
                <form onSubmit={handleSubmitLead} className="lead-form">
                  <input
                    type="text"
                    placeholder="Tu nombre *"
                    value={leadData.nombre}
                    onChange={(e) => setLeadData({ ...leadData, nombre: e.target.value })}
                    required
                    className="form-input"
                  />
                  <input
                    type="email"
                    placeholder="Tu email *"
                    value={leadData.email}
                    onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                    required
                    className="form-input"
                  />
                  <input
                    type="tel"
                    placeholder="Teléfono (opcional)"
                    value={leadData.telefono}
                    onChange={(e) => setLeadData({ ...leadData, telefono: e.target.value })}
                    className="form-input"
                  />
                  <input
                    type="text"
                    placeholder="Empresa (opcional)"
                    value={leadData.empresa}
                    onChange={(e) => setLeadData({ ...leadData, empresa: e.target.value })}
                    className="form-input"
                  />
                  <label className="privacy-checkbox">
                    <input
                      type="checkbox"
                      checked={leadData.acceptPrivacy}
                      onChange={(e) => setLeadData({ ...leadData, acceptPrivacy: e.target.checked })}
                      required
                    />
                    <span>
                      Acepto la <a href="/privacidad" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
                    </span>
                  </label>
                  <div className="form-actions">
                    <button type="submit" className="form-submit" disabled={!leadData.acceptPrivacy}>Enviar</button>
                    <button type="button" onClick={() => setShowLeadForm(false)} className="form-cancel">Cancelar</button>
                  </div>
                </form>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="input-bar" onClick={() => inputRef.current?.focus()}>
            <span className="prompt-mark">&gt;</span>
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje"
              className="chat-input"
              rows="1"
              disabled={isLoading || isRevealing}
            />
            <button
              onClick={() => sendMessage()}
              disabled={isLoading || isRevealing || !input.trim()}
              className="send-button"
              aria-label="Enviar mensaje"
            >
              →
            </button>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-content">
          <p className="footer-info">
            DESDE CHILE, ESTADOS UNIDOS, ARGENTINA Y TODO EL MUNDO - CONTACTO: AGENCIA DE PUBLICIDAD LA BOTICA LIMITADA - BADAJOZ 100, OFICINA 523, LAS CONDES - <a href="mailto:hola@agencialabotica.cl" className="footer-mail">HOLA@AGENCIALABOTICA.CL</a>
          </p>
          <div className="footer-legal">
            <a href="/privacidad">Política de Privacidad</a>
            <span className="legal-separator">|</span>
            <a href="/terminos">Términos y Condiciones</a>
            <span className="legal-separator">|</span>
            <a href="/eliminacion-datos">Eliminación de Datos</a>
          </div>
          <SocialLinks className="social-links footer-social" />
        </div>
      </footer>

      {showCookieBanner && (
        <div className="cookie-banner">
          <div className="cookie-content">
            <p>
              Utilizamos cookies para mejorar tu experiencia en nuestro sitio web.
              Al continuar navegando, aceptas nuestra <a href="/privacidad">Política de Privacidad</a>.
            </p>
            <div className="cookie-actions">
              <button onClick={handleAcceptCookies} className="cookie-accept">Aceptar</button>
              <a href="/privacidad" className="cookie-more">Más información</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  return <Home />;
}

export default App;
